<template>
    <div
        v-if="isVisible"
        ref="contextMenu"
        class="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[160px] context-menu"
        :style="menuStyle"
        @click.stop
    >
        <SceneDropdownComponent
            v-if="!isTrashedFolder && hasAvailableScenes"
            :folders="folders"
            :photo="photo"
            @scene-selected="handleSceneSelected"
        />

        <button
            v-if="!isTrashedFolder && !photo.is_cover"
            @click="handleMakeCover"
            class="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
        >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"></path>
            </svg>
            {{ $t('files.context_menu.make_cover') }}
        </button>

        <button
            v-if="isTrashedFolder"
            @click="handleRecover"
            class="w-full px-4 py-2 text-left text-sm text-royal-blue-500 hover:bg-royal-blue-50 flex items-center gap-2"
        >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3"></path>
            </svg>
            {{ $t('files.context_menu.recover') }}
        </button>

        <button
            v-if="!isTrashedFolder"
            @click="handleDelete"
            class="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
        >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
            {{ $t('files.context_menu.delete') }}
        </button>

        <button
            v-if="isTrashedFolder"
            @click="handleDelete"
            class="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
        >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
            {{ $t('files.context_menu.delete') }}
        </button>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Photo } from '@/types/PhotoType';
import { Folder } from '@/types/GalleryType';
import SceneDropdownComponent from './SceneDropdownComponent.vue';

export default defineComponent({
    name: 'ContextMenuComponent',

    components: {
        SceneDropdownComponent
    },

    props: {
        photo: {
            type: Object as () => Photo,
            required: true
        },
        folders: {
            type: Array as () => Folder[],
            default: () => []
        },
        isVisible: {
            type: Boolean,
            default: false
        },
        position: {
            type: Object as () => { x: number; y: number },
            default: () => ({ x: 0, y: 0 })
        },
        isTrashedFolder: {
            type: Boolean,
            default: false
        }
    },

    emits: ['close', 'delete', 'recover', 'add-to-scene', 'make-cover'],

    data() {
        return {
            contextMenu: null as HTMLElement | null
        };
    },

    computed: {
        menuStyle(): { left: string; top: string } {
            return {
                left: `${this.position.x}px`,
                top: `${this.position.y}px`,
            };
        },

        hasAvailableScenes(): boolean {
            const folders = this.folders as any[];
            const photoFolders = this.photo?.folders || [];
            return folders.some((folder: any) => {
                return !photoFolders.some((photoFolder: any) => photoFolder.id === folder.id);
            });
        }
    },

    methods: {
        handleDelete() {
            this.$emit('delete', this.photo);
            this.$emit('close');
        },

        handleRecover() {
            this.$emit('recover', this.photo);
            this.$emit('close');
        },

        handleSceneSelected({ photo, folder }: { photo: Photo; folder: any }) {
            this.$emit('add-to-scene', { photo, folder });
            this.$emit('close');
        },

        handleMakeCover() {
            this.$emit('make-cover', this.photo);
            this.$emit('close');
        },

        handleClickOutside(event: MouseEvent) {
            if (this.contextMenu && !this.contextMenu.contains(event.target as Node)) {
                this.$emit('close');
            }
        },

        handleMouseDown(event: MouseEvent) {
            if (this.contextMenu && !this.contextMenu.contains(event.target as Node)) {
                this.$emit('close');
            }
        },

        handleDocumentClick(event: MouseEvent) {
            if (this.contextMenu && !this.contextMenu.contains(event.target as Node)) {
                this.$emit('close');
            }
        },

        handleEscape(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                this.$emit('close');
            }
        }
    },

    mounted() {
        this.contextMenu = this.$refs.contextMenu as HTMLElement;
        // Використовуємо setTimeout для того, щоб не закрити меню одразу після відкриття
        setTimeout(() => {
            document.addEventListener('click', this.handleDocumentClick, true);
            document.addEventListener('mousedown', this.handleMouseDown, true);
        }, 0);
        document.addEventListener('keydown', this.handleEscape);
    },

    beforeUnmount() {
        document.removeEventListener('click', this.handleDocumentClick, true);
        document.removeEventListener('mousedown', this.handleMouseDown, true);
        document.removeEventListener('keydown', this.handleEscape);
    }
});
</script>

<style scoped>
/* Додаткові стилі для контекстного меню */
.fixed {
    position: fixed;
    z-index: 50;
}

/* Переконаємося, що меню завжди видиме */
.context-menu {
    pointer-events: auto;
}
</style>
